import React from 'react';
import { IconTOC } from '@bynder/icons';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import { Translate } from '@bynder/localization';
import { Flex, token, Button, Thumbnail } from '@bynder/design-system';
import { ModalType } from '../../types';
import { MaxWidthContainer } from '../shared/styled-components/Page';
import { ROUTES } from '../../constants/app';
import { useActivePage } from '../../queries/guides';
import { usePageMutations } from '../../mutations/pages';
import { TableOfContents } from '../../types/tableOfContents';
import { useGuideStore } from '../../stores/guideStore';
import { useModalStore } from '../../stores/modalStore';
import { useSidebarStore } from '../../stores/sidebarStore';

const PageTableOfContents: React.FC = () => {
  const history = useHistory();
  const activeGuideId = useGuideStore(s => s.activeGuideId);
  const isEditMode = useGuideStore(s => s.isEditMode);
  const { data: page } = useActivePage();
  const tableOfContents = page?.tableOfContents;
  const tocEntries = useSidebarStore(state => state.tableOfContents);

  if (isEditMode && tableOfContents?.isVisibleInPage) {
    return <ToCPagePlaceholder tableOfContents={tableOfContents} />;
  }

  return tableOfContents.isVisibleInPage && tocEntries.length > 0 ? (
    <StyledContainer data-testid="tableOfContentsTestId">
      <StyledTableOfContents>
        {tocEntries.map((entry, index) => {
          return (
            <StyledButton
              data-testid={`testId-${index}`}
              onClick={() =>
                history.push({
                  pathname: ROUTES.GUIDE_PAGE(activeGuideId, page?.id),
                  hash: entry.key,
                })
              }
              variant="clean"
              key={entry.key}
            >
              {entry.text}
            </StyledButton>
          );
        })}
      </StyledTableOfContents>
    </StyledContainer>
  ) : null;
};

export default PageTableOfContents;

const StyledContainer = styled(MaxWidthContainer)`
  padding-top: ${token.spacing9};
  padding-bottom: ${token.spacing6};
  margin-bottom: ${token.spacing7};
`;

const StyledTableOfContents = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const StyledButton = styled(Button)`
  font-size: ${token.fontSize100};
  padding: ${token.spacing3} ${token.spacing4};
  &:hover {
    background-color: ${token.gray50a};
  }
`;

const ToCPagePlaceholder: React.FC<{ tableOfContents: TableOfContents }> = ({
  tableOfContents,
}) => {
  const openModal = useModalStore.use.openModal();

  const { editTableOfContentsVisibility } = usePageMutations();
  const handleClick = () => {
    const onModalSubmit = (
      isVisibleInGuide: boolean,
      isVisibleInPage: boolean,
    ) =>
      editTableOfContentsVisibility.mutate({
        isVisibleInGuide,
        isVisibleInPage,
      });

    const modalProps = {
      visibleInGuide: tableOfContents?.isVisibleInGuide,
      visibleInPage: tableOfContents?.isVisibleInPage,
    };

    openModal({
      modalType: ModalType.TABLE_OF_CONTENTS_MODAL,
      modalOnSubmit: onModalSubmit,
      modalProps,
    });
  };

  return (
    <StyledContainer data-testid="tocPlaceholderId">
      <StyledPlaceHolder>
        <Flex alignItems="center" gap="3">
          <Thumbnail
            variant="clean"
            icon={<IconTOC />}
            shape="circle"
            size="m"
          />
          <p>
            <Translate id="PAGE_TABLE_OF_CONTENTS.PLACEHOLDER_TITLE" />
          </p>
        </Flex>
        <Button onClick={handleClick} data-testid="tocMenu">
          <Translate id="PAGE_TABLE_OF_CONTENTS.EDIT_BUTTON_TEXT" />
        </Button>
      </StyledPlaceHolder>
    </StyledContainer>
  );
};

const StyledPlaceHolder = styled.div`
  height: 72px;
  border: 1px solid ${token.gray100a};
  border-radius: ${token.radiusLarge};
  display: flex;
  padding: 0 ${token.spacing5};
  align-items: center;
  justify-content: space-between;
  p {
    color: ${token.gray500a};
    justify-self: flex-start;
  }
`;
